import {FC, useCallback, useEffect, useRef, useState} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {Box, CircularProgress, InputAdornment, List, ListItemButton, TextField, Typography,} from '@mui/material';
import {Search} from '@mui/icons-material';
import {useIntl} from 'react-intl';
import AMPModal from 'src/components/common/AMPModal';
import {useAppSelector} from 'src/app/hooks';
import {tagsApi} from 'src/api';
import {TagsDTO} from 'src/types/tags';
import {SelectOcppTagModalProps} from './SelectOcppTagModal.types';
import {listItemSx, listSx} from './SelectOcppTagModal.sx';

interface TagListItem {
    id: number;
    idTag: string;
    ownerInfo: string;
}

const PAGE_SIZE = 100;

const getTagOwnerInfo = (tag: TagsDTO): string => {
    // Employee tag - show employee name + company
    if (tag.employee) {
        const employeeName = `${tag.employee.firstName || ''} ${tag.employee.lastName || ''}`.trim();
        const companyName = tag.company?.name;
        if (employeeName && companyName) {
            return `${employeeName}    •    ${companyName}`;
        }
        return employeeName || companyName || '';
    }

    // Company tag - show company name
    if (tag.company?.name) {
        return tag.company.name;
    }

    // Private driver tag - show first user's name
    if (tag.users && tag.users.length > 0) {
        const user = tag.users[0];
        const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
        return userName || user.emailAddress || '';
    }

    return '';
};

export const SelectOcppTagModal: FC<SelectOcppTagModalProps> = ({
                                                                    open,
                                                                    handleClose,
                                                                    onSelectTag,
                                                                    businessId,
                                                                    companyId,
                                                                }) => {
    const intl = useIntl();
    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);

    const isDriver = persona.isDriver;
    const isEmployee = persona.isEmployee;
    const [searchKeyword, setSearchKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [page, setPage] = useState(0);
    const [tags, setTags] = useState<TagListItem[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const listRef = useRef<HTMLUListElement>(null);

    // Debounce search keyword
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedKeyword(searchKeyword);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchKeyword]);

    // Fetch tags from API
    const [fetchTags, tagsResult] = tagsApi.useLazyGetTagsQuery();

    const getSearchData = useCallback(() => {
        const baseData: Record<string, unknown> = {businessId};

        if (debouncedKeyword) {
            baseData.keyword = debouncedKeyword;
        }

        // The tag search is clamped by the server, which forces driverId for a driver and employeeId
        // for an employee - the two names the owner of a tag goes by. A company passing its own id
        // here was the same value the server writes anyway.
        if (companyId) {
            baseData.companyId = companyId;
        }

        return baseData;
    }, [businessId, debouncedKeyword, isDriver, isEmployee, authInfo, companyId]);

    const loadTags = useCallback(
        (pageNum: number, reset = false) => {
            fetchTags({
                params: {page: pageNum, size: PAGE_SIZE},
                data: getSearchData(),
            }).then((result) => {
                if (result.data?.content) {
                    const newTags = result.data.content
                        .filter((tag) => !tag.blocked)
                        .map((tag) => ({
                            id: tag.id,
                            idTag: tag.idTag,
                            ownerInfo: getTagOwnerInfo(tag),
                        }));

                    setTags((prev) => (reset ? newTags : [...prev, ...newTags]));
                    setHasMore(result.data.page.number < result.data.page.totalPages - 1);
                }
            });
        },
        [fetchTags, getSearchData]
    );

    // Reset and load when modal opens or search changes
    useEffect(() => {
        if (open) {
            setPage(0);
            setTags([]);
            setHasMore(true);
            loadTags(0, true);
        }
    }, [open, debouncedKeyword]);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            setSearchKeyword('');
            setDebouncedKeyword('');
            setPage(0);
            setTags([]);
        }
    }, [open]);

    const handleScroll = useCallback(() => {
        if (!listRef.current || tagsResult.isLoading || !hasMore) return;

        const {scrollTop, scrollHeight, clientHeight} = listRef.current;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            const nextPage = page + 1;
            setPage(nextPage);
            loadTags(nextPage);
        }
    }, [page, loadTags, tagsResult.isLoading, hasMore]);

    const isLoading = tagsResult.isLoading && tags.length === 0;
    const isLoadingMore = tagsResult.isLoading && tags.length > 0;

    const handleSelectTag = (idTag: string) => {
        onSelectTag(idTag);
        handleClose();
    };

    return (
        <AMPModal
            handleClose={handleClose}
            open={open}
            title={intl.formatMessage({id: 'selectOcppTag'})}
            type="sm"
            fullWidth
        >
            <Box>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={intl.formatMessage({id: 'search'})}
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search/>
                            </InputAdornment>
                        ),
                    }}
                    sx={{mb: 2}}
                />

                <Box sx={{height: 300, position: 'relative'}}>
                    {isLoading && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <CircularProgress size={40}/>
                        </Box>
                    )}

                    {!isLoading && tags.length > 0 && (
                        <List
                            ref={listRef}
                            onScroll={handleScroll}
                            sx={{
                                ...listSx,
                                height: '100%',
                                overflow: 'auto',
                            }}
                        >
                            {tags.map((tag) => (
                                <ListItemButton
                                    key={tag.id}
                                    sx={listItemSx}
                                    onClick={() => handleSelectTag(tag.idTag)}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Box sx={{width: '50%', display: 'flex', alignItems: 'baseline', minWidth: 0}}>
                                            <Typography
                                                variant="caption"
                                                sx={{mr: 1, color: 'grey.600', flexShrink: 0}}
                                            >
                                                {intl.formatMessage({id: 'rfidLabel'})}:
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {tag.idTag}
                                            </Typography>
                                        </Box>
                                        {tag.ownerInfo && (
                                            <Box sx={{
                                                width: '50%',
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                minWidth: 0
                                            }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{mr: 1, color: 'grey.600', flexShrink: 0}}
                                                >
                                                    {intl.formatMessage({id: 'clientLabel'})}:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 500,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {tag.ownerInfo.split('•').map((part, index, arr) => (
                                                        <span key={index}>
                              {part.trim()}
                                                            {index < arr.length - 1 && (
                                                                <span
                                                                    style={{margin: '0 12px', fontWeight: 400}}>•</span>
                                                            )}
                            </span>
                                                    ))}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </ListItemButton>
                            ))}
                            {isLoadingMore && (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 2}}>
                                    <CircularProgress size={24}/>
                                </Box>
                            )}
                        </List>
                    )}

                    {!isLoading && tags.length === 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <Typography variant="h6">
                                {intl.formatMessage({id: 'noTagsAvailable'})}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </AMPModal>
    );
};